import { useMutation } from '@tanstack/react-query';
import { Action, AmountLineItem, Badge, ConfirmModal } from 'corso-admin-ui';
import { useCallback, useState } from 'react';
import api from '~/api';
import {
  ClaimReview,
  useClaim,
} from '~/features/claimReview/ClaimReviewProvider';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { isErrorMessage } from '~/utils/errorResponse';
import { formatter } from 'corso-web-lib';

type Payment = NonNullable<ClaimReview['customerPayment']>;

function RefundPayment({ payment }: { payment: Payment }) {
  const [show, setShow] = useState(false);
  const close = useCallback(() => setShow(false), []);
  const open = useCallback(() => setShow(true), []);

  const storeId = useStoreId();
  const claim = useClaim();

  const { amount, currencyCode, status } = payment;

  const toast = useToast();

  const { mutate: refundPayment, isPending } = useMutation({
    mutationKey: [payment.id],
    mutationFn: () => api.store(storeId).payment.refund(`${payment.id}`),
    onSuccess: () => claim.refetch(),
    onError: (error) => {
      const message = isErrorMessage(error)
        ? error.message
        : 'Failed to refund payment. Please try again later.';

      toast.show(message);
    },
  });

  return (
    <>
      {status === 'Captured' && (
        <Action onClick={open} className="text-xs">
          Refund on Stripe
        </Action>
      )}
      <ConfirmModal
        title="Refund Payment"
        prompt={`Are you sure you want to process a refund for ${formatter.currency(
          amount,
          currencyCode,
        )} on Stripe?`}
        confirmText="Refund"
        cancelText="Cancel"
        show={show}
        loading={isPending}
        onConfirm={() => {
          refundPayment();
          close();
        }}
        onCancel={close}
      />
    </>
  );
}

export function PaymentLineItem({ payment }: { payment: Payment }) {
  return (
    <AmountLineItem
      size="sm"
      media={{ icon: 'payment' }}
      title="Payment"
      currencyCode={payment.currencyCode}
      amount={payment.status === 'Refunded' ? 0 : payment.amount}
    >
      <AmountLineItem.Detail>
        {payment.note}
        <Badge>{payment.status}</Badge>
        <RefundPayment payment={payment} />
      </AmountLineItem.Detail>
    </AmountLineItem>
  );
}
